import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { storeProducts } from '@/mocks/storeProducts';

const storeIcons = [
  { icon: 'ri-shirt-line', label: 'home.store.marqueeFashion' },
  { icon: 'ri-heart-pulse-line', label: 'home.store.marqueeSupplements' },
  { icon: 'ri-drop-line', label: 'home.store.marqueeAccessories' },
  { icon: 'ri-boxing-line', label: 'home.store.marqueeTraining' },
  { icon: 'ri-flashlight-line', label: 'home.store.marqueeBodybuilding' },
  { icon: 'ri-cup-line', label: 'home.store.marqueePerformance' },
  { icon: 'ri-shirt-line', label: 'home.store.marqueeFashion' },
  { icon: 'ri-heart-pulse-line', label: 'home.store.marqueeSupplements' },
  { icon: 'ri-drop-line', label: 'home.store.marqueeAccessories' },
  { icon: 'ri-boxing-line', label: 'home.store.marqueeTraining' },
  { icon: 'ri-flashlight-line', label: 'home.store.marqueeBodybuilding' },
  { icon: 'ri-cup-line', label: 'home.store.marqueePerformance' },
];

export default function FitnessStoreSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 80}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="loja" className="bg-primary-500 py-16 md:py-24">
      <div className="w-full overflow-hidden mb-10 md:mb-14 bg-background-50 py-4 md:py-5">
        <div className="flex animate-marquee whitespace-nowrap">
          {storeIcons.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 mx-6 md:mx-10"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-foreground-950/10">
                <i className={`${item.icon} text-foreground-950 text-lg md:text-xl`}></i>
              </div>
              <span className="font-body text-sm md:text-base font-semibold text-foreground-950 uppercase tracking-wider">
                {t(item.label)}
              </span>
              <span className="text-foreground-800/40 mx-2">·</span>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div>
            <span className="reveal-item reveal font-body text-sm text-foreground-800 uppercase tracking-widest">
              {t('home.store.label')}
            </span>
            <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 leading-tight mt-2">
              {t('home.store.title')}
            </h2>
          </div>
          <p className="reveal-item reveal font-body text-base text-foreground-800 max-w-md">
            {t('home.store.subtitle')}
          </p>
        </div>

        <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {storeProducts.map((product) => (
            <div
              key={product.id}
              className="reveal-item reveal group bg-background-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="aspect-[3/4] w-full overflow-hidden relative">
                <img
                  src={product.image}
                  alt={t(`store.product.${product.id}.name`)}
                  loading="lazy"
                  fetchPriority="low"
                  width="600"
                  height="800"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                />
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-body text-base font-semibold text-foreground-950 leading-tight">
                  {t(`store.product.${product.id}.name`)}
                </h3>
                <p className="font-body text-sm text-foreground-500 mt-2">
                  {t(product.category)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
